import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IssueReportAPI } from '../api/IssueReportAPI';
import ConfirmDeleteModal from './dashboard/components/ConfirmDeleteModal';
import '../pages/dashboard/components/Marker-UI.css';
import './IssueKanban.css';
import PageTitle from '../components/PageTitle';

// ── Column config ─────────────────────────────────────
const COLUMNS = [
  { id: 'backlog',    label: 'Offen',     accent: '#9ca3af' },
  { id: 'in_process', label: 'In Arbeit', accent: '#006cfd' },
  { id: 'testing',    label: 'Im Test',   accent: '#d97706' },
  { id: 'done',       label: 'Erledigt',  accent: '#16a34a' },
];

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Component ─────────────────────────────────────────
export default function IssueKanban() {
  const [issues, setIssues]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [dragOverCol, setDragOverCol]     = useState(null);
  const [draggingId, setDraggingId]       = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  // ── create-issue state ────────────────────────────
  const [creating, setCreating]     = useState(false);
  const [createText, setCreateText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const textareaRef = useRef(null);

  // ── fetch ──────────────────────────────────────────
  const fetchIssues = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await IssueReportAPI.getAll();
      setIssues(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  // Focus textarea when form opens
  useEffect(() => {
    if (creating) textareaRef.current?.focus();
  }, [creating]);

  // ── create ─────────────────────────────────────────
  const openCreate = () => {
    setCreateText('');
    setCreateError('');
    setCreating(true);
  };

  const cancelCreate = () => {
    setCreating(false);
    setCreateText('');
    setCreateError('');
  };

  const handleCreate = async () => {
    const msg = createText.trim();
    if (!msg) { setCreateError('Nachricht darf nicht leer sein.'); return; }

    setSubmitting(true);
    setCreateError('');
    try {
      const created = await IssueReportAPI.create(msg);
      setIssues(all => [created, ...all]);
      cancelCreate();
    } catch (e) {
      setCreateError(e?.response?.data?.message || e?.message || 'Erstellen fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const onTextareaKey = (e) => {
    if (e.key === 'Escape') { cancelCreate(); return; }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleCreate();
  };

  // ── drag-and-drop ──────────────────────────────────
  const onDragStart = (e, id) => {
    e.dataTransfer.setData('issueId', String(id));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(id);
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverCol(null);
  };

  const onDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCol !== colId) setDragOverCol(colId);
  };

  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null);
  };

  const onDrop = async (e, colId) => {
    e.preventDefault();
    setDragOverCol(null);
    const id = Number(e.dataTransfer.getData('issueId'));
    const issue = issues.find(i => i.id === id);
    if (!issue || issue.status === colId) return;

    const prevStatus = issue.status;
    setIssues(all => all.map(i => i.id === id ? { ...i, status: colId } : i));
    try {
      await IssueReportAPI.updateStatus(id, colId);
    } catch {
      setIssues(all => all.map(i => i.id === id ? { ...i, status: prevStatus } : i));
    }
  };

  // ── delete ─────────────────────────────────────────
  const onDeleteConfirm = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ open: false, id: null });
    setIssues(all => all.filter(i => i.id !== id));
    try {
      await IssueReportAPI.delete(id);
    } catch {
      fetchIssues();
    }
  };

  // ── render ─────────────────────────────────────────
  return (
    <div className="page">

      {/* ── Header ────────────────────────────── */}
      <div className="page-header">
        <PageTitle title="Meldungen" description="Meldungen von Nutzern und eigene Aufgaben. Zieh eine Karte in die nächste Spalte, wenn sich ihr Stand ändert." />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn-add btn-add--green"
            type="button"
            onClick={openCreate}
            disabled={creating}
          >
            <svg className="btn-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Neue Meldung
          </button>
          <button
            className="btn-add btn-add--white"
            type="button"
            onClick={fetchIssues}
            aria-label="Aktualisieren"
          >
            <svg className="btn-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 1 0 9-9" />
              <path d="M3 3v6h6" />
            </svg>
            Aktualisieren
          </button>
        </div>
      </div>

      {/* ── Meta ──────────────────────────────── */}
      {!loading && !error && (
        <div className="kb-meta">
          <span className="kb-meta-total">{issues.length} {issues.length === 1 ? 'Meldung' : 'Meldungen'} gesamt</span>
        </div>
      )}

      {/* ── Loading ───────────────────────────── */}
      {loading && (
        <div className="loading-state">
          <div className="spinner" role="status" aria-label="Lädt Meldungen" />
          <span className="muted">Lade Meldungen …</span>
        </div>
      )}

      {/* ── Error ─────────────────────────────── */}
      {error && (
        <div className="error-state" role="alert">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            width="17" height="17" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
          <button className="btn btn-ghost"
            style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}
            onClick={fetchIssues}>
            Erneut versuchen
          </button>
        </div>
      )}

      {/* ── Board ─────────────────────────────── */}
      {!loading && !error && (
        <div className="kb-board">
          {COLUMNS.map(col => {
            const colIssues = issues.filter(i => i.status === col.id);
            const isBacklog = col.id === 'backlog';

            return (
              <div
                key={col.id}
                className={`kb-col${dragOverCol === col.id ? ' kb-col--over' : ''}`}
                style={{ '--col-accent': col.accent }}
                onDragOver={(e) => onDragOver(e, col.id)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, col.id)}
              >
                {/* column header */}
                <div className="kb-col-head">
                  <span className="kb-col-dot" aria-hidden="true" />
                  <span className="kb-col-title">{col.label}</span>
                  <span className="kb-col-badge">{colIssues.length}</span>
                  {isBacklog && (
                    <button
                      className="kb-col-add"
                      type="button"
                      onClick={openCreate}
                      title="Neue Meldung anlegen"
                      aria-label="Neue Meldung anlegen"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        width="11" height="11" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* cards */}
                <div className="kb-cards">

                  {/* ── inline create form (Backlog only) ── */}
                  {isBacklog && creating && (
                    <div className="kb-create-card">
                      <textarea
                        ref={textareaRef}
                        className="kb-create-textarea"
                        placeholder="Was ist zu tun? Kurz beschreiben …"
                        value={createText}
                        onChange={e => { setCreateText(e.target.value); setCreateError(''); }}
                        onKeyDown={onTextareaKey}
                        rows={4}
                        disabled={submitting}
                      />
                      {createError && (
                        <div className="kb-create-error">{createError}</div>
                      )}
                      <div className="kb-create-hint">
                        <kbd>Ctrl</kbd><span>+</span><kbd>↵</kbd> zum Speichern · <kbd>Esc</kbd> zum Abbrechen
                      </div>
                      <div className="kb-create-actions">
                        <button
                          className="kb-create-submit"
                          type="button"
                          onClick={handleCreate}
                          disabled={submitting || !createText.trim()}
                        >
                          {submitting
                            ? <><span className="kb-create-spinner" />Erstellen…</>
                            : 'Erstellen'
                          }
                        </button>
                        <button
                          className="kb-create-cancel"
                          type="button"
                          onClick={cancelCreate}
                          disabled={submitting}
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}

                  {colIssues.map(issue => (
                    <div
                      key={issue.id}
                      className={`kb-card${draggingId === issue.id ? ' kb-card--dragging' : ''}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, issue.id)}
                      onDragEnd={onDragEnd}
                    >
                      <div className="kb-card-row">
                        <span className="kb-card-id">#{issue.id}</span>
                        <button
                          className="kb-del"
                          type="button"
                          onClick={() => setConfirmDelete({ open: true, id: issue.id })}
                          title="Meldung löschen"
                          aria-label="Meldung löschen"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                            width="11" height="11" aria-hidden="true">
                            <path d="M18 6 6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      <p className="kb-card-msg">{issue.message}</p>

                      {issue.createdAt && (
                        <time className="kb-card-date" dateTime={issue.createdAt}>
                          {fmtDate(issue.createdAt)}
                        </time>
                      )}
                    </div>
                  ))}

                  {colIssues.length === 0 && !creating && (
                    <div className="kb-empty" aria-label="Keine Meldungen in dieser Spalte">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                        width="18" height="18" aria-hidden="true">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M3 9h18" />
                      </svg>
                      <span>Nichts hier</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Confirm delete ────────────────────── */}
      <ConfirmDeleteModal
        open={confirmDelete.open}
        title="Meldung löschen?"
        message="Die Meldung wird dauerhaft gelöscht. Das lässt sich nicht rückgängig machen."
        confirmLabel="Löschen"
        onConfirm={onDeleteConfirm}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />
    </div>
  );
}
